import { App, URLOpenListenerEvent } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { PluginListenerHandle } from "@capacitor/core";
import * as Sentry from "@sentry/react";
import Keycloak, { KeycloakAdapter } from "keycloak-js";
import React from "react";
import { SplashScreen } from "../../common/SplashScreen.js";
import { AuthContext, tokenBelongsToGroup } from "./AuthContext.js";
import { keycloakCapacitorTokenStorage } from "./keycloakCapacitorTokenStorage.js";
import { SIGNUP_SCOPE } from "./signup/signupScope.js";
import { installSignupRoleRequestParam } from "./signupRoleRequestParam.js";

interface Props extends React.PropsWithChildren<{}> {
  keycloak: Keycloak;
}

export const AuthCapacitorProvider = ({ children, keycloak }: Props) => {
  const [isReady, setIsReady] = React.useState(false);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);

  React.useEffect(() => {
    function setIsAuthenticatedState() {
      setIsAuthenticated(keycloak.authenticated ?? false);
    }

    function persistTokens() {
      if (keycloak.token && keycloak.refreshToken) {
        void keycloakCapacitorTokenStorage.write({
          token: keycloak.token,
          refreshToken: keycloak.refreshToken,
          idToken: keycloak.idToken,
        });
      }
    }

    function clearTokens() {
      void keycloakCapacitorTokenStorage.clear();
    }

    async function bootstrap() {
      try {
        // The fixed native callback below carries no `ot`; the role travels
        // as an authorization request parameter instead.
        installSignupRoleRequestParam(keycloak);
        keycloak.onReady = () => {
          setIsReady(true);
        };
        keycloak.onAuthError = () => {
          setIsAuthenticatedState();
        };
        keycloak.onAuthLogout = () => {
          clearTokens();
          setIsAuthenticatedState();
        };
        keycloak.onAuthRefreshError = () => {
          clearTokens();
          setIsAuthenticatedState();
        };
        keycloak.onTokenExpired = () => {
          // Rotate the access token using the refresh token; the resulting
          // onAuthRefreshSuccess / onAuthRefreshError event drives state and
          // persistence. Without this, an expired access token would never be
          // refreshed and the user would be silently logged out.
          keycloak.updateToken(5).catch(e => {
            clearTokens();
            Sentry.captureException(e);
          });
        };
        keycloak.onAuthSuccess = () => {
          persistTokens();
          setIsAuthenticatedState();
        };
        keycloak.onAuthRefreshSuccess = () => {
          persistTokens();
          setIsAuthenticatedState();
        };

        const storedTokens = await keycloakCapacitorTokenStorage.read();

        await keycloak.init({
          // reference: https://github.com/jy95/keycloak-capacitor/blob/e0b3b4095826864e4d72f5cd061a20bce2a9341f/src/keycloak.js#L75
          adapter: getKeycloakCapacitorAdapter(keycloak),
          checkLoginIframe: false,
          responseMode: "query",
          onLoad: "check-sso",
          // flow: "hybrid", // 'implicit', 'hybrid',
          redirectUri: "de.meistersystems:/keycloak",
          // Default scope of every login/registration URL the adapter opens
          // (see the web provider).
          scope: SIGNUP_SCOPE,
          token: storedTokens?.token,
          refreshToken: storedTokens?.refreshToken,
          idToken: storedTokens?.idToken,
        });
      } catch (e) {
        if (e instanceof Error)
          console.error("Failed to initialize keycloak: ", e.message);
        Sentry.captureException(e);
      }
    }

    void bootstrap();
  }, [keycloak]);

  if (!isReady) return <SplashScreen />;

  return (
    <AuthContext.Provider
      value={{
        isReady,
        isAuthenticated,
        belongsToGroup: (groupName: string) =>
          tokenBelongsToGroup(keycloak.idTokenParsed, groupName),
        auth: keycloak,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

interface Deferred<T> {
  promise: Promise<T>;
  resolve: (value: T) => void;
  reject: (error: Error) => void;
}

// keycloak-js 26 dropped the internal `keycloak.createPromise` helper (it uses
// native promises everywhere), so the adapter builds its own deferred to bridge
// the Capacitor deep-link callback into the promise the adapter method returns.
function createDeferred<T>(): Deferred<T> {
  let resolve!: (value: T) => void;
  let reject!: (error: Error) => void;
  const promise = new Promise<T>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

function toError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}

// How long a deep link may still arrive after the browser reports it closed.
// Android reports the Custom Tab closing when the Keycloak redirect re-opens
// the app, in no guaranteed order relative to `appUrlOpen`.
const BROWSER_CLOSED_GRACE_MS = 1500;

interface AuthAttempt {
  deferred: Deferred<void>;
  isSuperseded: () => boolean;
  /** Finishes the attempt as cancelled, without closing any browser. */
  supersede: () => void;
}

// Supersedes the newest attempt that has not finished yet.
let supersedePendingAttempt: (() => void) | undefined;

/**
 * Starts a login / register / logout attempt. Only the newest attempt may
 * open a browser or consume a deep link: an earlier one still pending (e.g. in
 * its grace period after the user closed the browser, or still creating its
 * URL) finishes as cancelled, so two handlers never process the same callback
 * and overwrite each other's state / PKCE entry. Call it synchronously when
 * the adapter method is invoked, before awaiting the URL, so attempts are
 * ordered by invocation.
 */
function beginAuthAttempt(): AuthAttempt {
  supersedePendingAttempt?.();
  const deferred = createDeferred<void>();
  let superseded = false;
  const supersede = () => {
    superseded = true;
    deferred.resolve(undefined);
  };
  supersedePendingAttempt = supersede;
  void deferred.promise.finally(() => {
    if (supersedePendingAttempt === supersede) {
      supersedePendingAttempt = undefined;
    }
  });
  return { deferred, isSuperseded: () => superseded, supersede };
}

async function openBrowserWithCallback(
  attempt: AuthAttempt,
  url: string,
  onDataCallback: (data: URLOpenListenerEvent) => void | Promise<void>
) {
  const { deferred, isSuperseded } = attempt;
  if (isSuperseded()) return;
  let addUrlListener: PluginListenerHandle | undefined;
  let browserFinishedListener: PluginListenerHandle | undefined;
  let closedTimer: ReturnType<typeof setTimeout> | undefined;
  let deepLinkReceived = false;

  void deferred.promise.finally(() => {
    clearTimeout(closedTimer);
    // Best-effort teardown — swallow rejections so a failed listener removal
    // or browser close does not surface as an unhandled promise rejection.
    void addUrlListener?.remove().catch(() => undefined);
    void browserFinishedListener?.remove().catch(() => undefined);
    // A superseded attempt's browser is already gone; closing would dismiss
    // the newer attempt's browser instead.
    if (!isSuperseded()) void Browser.close().catch(() => undefined);
  });

  try {
    // Registering the deep-link listener can itself reject (plugin
    // unavailable / init failure); keep it inside the try so a failure
    // rejects the deferred instead of hanging the fire-and-forget caller.
    addUrlListener = await App.addListener("appUrlOpen", data => {
      if (isSuperseded()) return;
      deepLinkReceived = true;
      clearTimeout(closedTimer);
      return onDataCallback(data);
    });
    // The user closed the browser (Done) without a deep link: finish the
    // attempt as cancelled, so check-sso completes (the app leaves the splash
    // screen) and this attempt's listeners are removed before the next one.
    browserFinishedListener = await Browser.addListener(
      "browserFinished",
      () => {
        if (isSuperseded() || deepLinkReceived) return;
        clearTimeout(closedTimer);
        closedTimer = setTimeout(
          () => deferred.resolve(undefined),
          BROWSER_CLOSED_GRACE_MS
        );
      }
    );
    if (isSuperseded()) return;
    // Full screen: iPadOS 27 never loads a popover-presented
    // SFSafariViewController (https://github.com/ionic-team/capacitor-plugins/issues/2595).
    await Browser.open({ url, presentationStyle: "fullscreen" });
  } catch (e) {
    const error = toError(e);
    Sentry.withScope(scope => {
      scope.setTag("service", "Capacitor");
      scope.setExtra("method", "openBrowserWithCallback");
      scope.setExtra("url", urlPathForSentry(url));
      Sentry.captureException(error);
    });
    // Always reject — a failed listener registration or a non-Error
    // rejection must not leave the adapter promise (and the browser/
    // listener) hanging.
    deferred.reject(error);
  }
}

// Strip query/hash from auth URLs before attaching to Sentry — they carry
// session state (state/nonce) and redirect params. Origin + path is enough.
function urlPathForSentry(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return "[unparsable-url]";
  }
}

async function openBrowser(deferred: Deferred<void>, url: string) {
  try {
    // Full screen, see openBrowserWithCallback.
    await Browser.open({ url, presentationStyle: "fullscreen" });
    deferred.resolve(undefined);
  } catch (e) {
    const error = toError(e);
    Sentry.withScope(scope => {
      scope.setTag("service", "Capacitor");
      scope.setExtra("method", "openBrowser");
      scope.setExtra("url", urlPathForSentry(url));
      Sentry.captureException(error);
    });
    deferred.reject(error);
  }
}

// references: https://github.com/edgeflare/keycloak-angular-capacitor/blob/master/projects/keycloak-angular-capacitor/src/lib/keycloak-angular-capacitor.service.ts
// taken from: https://github.com/jy95/keycloak-capacitor/blob/e0b3b4095826864e4d72f5cd061a20bce2a9341f/src/keycloak.js#L1668
const getKeycloakCapacitorAdapter = (keycloak: Keycloak): KeycloakAdapter => ({
  login: async options => {
    const attempt = beginAuthAttempt();
    const { deferred } = attempt;
    let loginUrl: string;
    try {
      // createLoginUrl is async in keycloak-js 26 (stores the PKCE verifier).
      loginUrl = await keycloak.createLoginUrl(options);
    } catch (e) {
      attempt.supersede();
      throw e;
    }

    void openBrowserWithCallback(attempt, loginUrl, async data => {
      try {
        const oauth = keycloak.parseCallback(data.url);
        await keycloak.processCallback(oauth);
        deferred.resolve(undefined);
      } catch (e) {
        deferred.reject(toError(e));
      }
    });

    return deferred.promise;
  },

  logout: options => {
    const attempt = beginAuthAttempt();
    const { deferred } = attempt;
    const logoutUrl = keycloak.createLogoutUrl(options);

    void openBrowserWithCallback(attempt, logoutUrl, () => {
      keycloak.clearToken();
      deferred.resolve(undefined);
    });

    return deferred.promise;
  },

  register: async options => {
    const attempt = beginAuthAttempt();
    const { deferred } = attempt;
    let registerUrl: string;
    try {
      // createRegisterUrl is async in keycloak-js 26 (stores the PKCE verifier).
      registerUrl = await keycloak.createRegisterUrl(options);
    } catch (e) {
      attempt.supersede();
      throw e;
    }

    void openBrowserWithCallback(attempt, registerUrl, async data => {
      try {
        const oauth = keycloak.parseCallback(data.url);
        await keycloak.processCallback(oauth);
        deferred.resolve(undefined);
      } catch (e) {
        deferred.reject(toError(e));
      }
    });

    return deferred.promise;
  },

  accountManagement: () => {
    const deferred = createDeferred<void>();
    const accountUrl = keycloak.createAccountUrl();
    if (!accountUrl) {
      throw new Error("Not supported by the OIDC server");
    }

    void openBrowser(deferred, accountUrl);

    return deferred.promise;
  },

  redirectUri: options => {
    if (options && options.redirectUri) {
      return options.redirectUri;
    } else if (keycloak.redirectUri) {
      return keycloak.redirectUri;
    } else {
      return "http://localhost";
    }
  },
});
